import { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { listAnnexure, errorMessage } from "../shared/api";

const formatCell = (column, value) => {
    if (_.isNil(value) || value === "") return <span className="text-gray-300">—</span>;
    if (column.type === "date") return format(new Date(value), "dd MMM yyyy");
    return value;
};

export default function AnnexureTable({ annexure }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const { data } = await listAnnexure(annexure.key);
            setRows(data);
        } catch (err) {
            setError(errorMessage(err, "Failed to load records"));
        } finally {
            setLoading(false);
        }
    }, [annexure.key]);

    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-gray-800">{annexure.title}</h2>
                <button onClick={load} disabled={loading} title="Refresh"
                    className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition disabled:opacity-50">
                    <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 px-5 py-3 text-sm text-rose-700">{error}</div>
            )}

            <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                {loading && <div className="px-6 py-12 text-center text-sm text-gray-400">Loading…</div>}
                {!loading && _.isEmpty(rows) && <div className="px-6 py-12 text-center text-sm text-gray-400">No records found.</div>}
                {!loading && !_.isEmpty(rows) && (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500 whitespace-nowrap">S. No.</th>
                                        {annexure.columns.map((column) => (
                                            <th key={column.key} className="px-6 py-3 text-left font-medium text-gray-500 min-w-[160px]">
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map((row, index) => (
                                        <tr key={row._id} className="hover:bg-gray-50 transition align-top">
                                            <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{index + 1}</td>
                                            {annexure.columns.map((column) => (
                                                <td key={column.key} className="px-6 py-4 text-gray-700 whitespace-pre-line">
                                                    {formatCell(column, row[column.key])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100">
                            <span className="text-xs text-gray-400">{rows.length} record{rows.length !== 1 ? "s" : ""} total</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
